import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { InvoiceModel, InvoiceModelV3 } from '@frontend/api-invoices';
import { useTranslation } from '@frontend/i18n';
import {
  CollectPaymentMultiModalInstance,
  usePaymentsModalButton,
  useValidPhoneNumberFromInvoice,
} from '@frontend/payments-collection-flow';
import { isPaidOrPendingInvoice, isPartialPayment } from '@frontend/payments-invoice-calculations';
import { useInvalidateInvoices, InvoiceCancelModal } from '@frontend/payments-invoice-controller';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  Button,
  CheckIconSmall,
  CopyIcon,
  DeleteIcon,
  ModalControlResponse,
  MoreIcon,
  PayIcon,
  PopoverMenu,
  PopoverMenuItem,
  Text,
  useModalControl,
  usePopoverMenu,
  useTooltip,
} from '@frontend/design-system';
import { usePaymentAlerts } from '../../hooks/navigation-alert/use-payment-alerts';
import { PaymentsNavAlerts } from '../../utils/helpers';
import { ConfirmCreateRequestModal } from './confirm-create-request-modal';

const styles = {
  collectPayment: css`
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `,
  autoWidth: css`
    width: auto;
  `,
};

type TooltipStylesProps = { copied: boolean };

const tooltipStyles = (props: TooltipStylesProps) => css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  .copy-status-icon {
    fill: ${props.copied ? theme.colors.success[50] : theme.colors.white};
  }
`;

type SummaryCardProps = {
  invoice: InvoiceModel;
  collectModalControl: ModalControlResponse;
  baseTrackingId?: string;
};

export const PaymentRequestActions = ({
  invoice,
  collectModalControl,
  baseTrackingId = 'pay-portal-invoice',
}: SummaryCardProps) => {
  const { t } = useTranslation('payments');
  const invalidateInvoices = useInvalidateInvoices();
  const navigate = useNavigate();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });

  const { firstValidPhoneNumber } = useValidPhoneNumberFromInvoice(invoice);

  const { modalProps: collectPaymentModalProps, triggerProps: collectPaymentModalTriggerProps } = collectModalControl;

  const { modalProps: createRequestModalProps, triggerProps: createRequestTriggerProps } = useModalControl();
  const { modalProps: deleteRequestModalProps, triggerProps: deleteRequestTriggerProps } = useModalControl();
  const {
    disabled: paymentsModalButtonDisabled,
    Tooltip: PaymentsModalTooltip,
    tooltipTriggerProps: paymentsModalTooltipTriggerProps,
  } = usePaymentsModalButton({ locationIds: [invoice.locationId] });

  const { showPaymentAlert } = usePaymentAlerts();

  const { getTriggerProps, getMenuProps, close } = usePopoverMenu({ placement: 'bottom-start' });
  const menuTriggerProps = getTriggerProps();
  const menuProps = getMenuProps();
  const { triggerProps: tooltipTriggerProps, tooltipProps, Tooltip } = useTooltip({ hoverDelay: 500 });

  const [copied, setCopied] = useState(false);

  const handleCollectPaymentClick = () => {
    collectPaymentModalTriggerProps.onClick();
  };

  const handleSendReminderClick = () => {
    collectPaymentModalTriggerProps.onClick();
    close();
  };
  const onRequestCancelled = useCallback(() => {
    navigate({ to: '/payments/invoices' });
  }, [navigate]);

  const onCreateRequest = useCallback(
    async (createdRequest: InvoiceModelV3) => {
      invalidateInvoices(invoice.locationId);

      showPaymentAlert(PaymentsNavAlerts.CreatePaymentRequest);
      navigate({ to: '/payments/invoices/$id', params: { id: createdRequest.id } });
    },
    [invoice, invalidateInvoices, navigate, showPaymentAlert]
  );

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied]);

  if (!collectPaymentModalProps.show) {
    if (isPaidOrPendingInvoice(invoice) && !isPartialPayment(invoice)) return null;

    if (isPartialPayment(invoice)) {
      return (
        <>
          <Button
            trackingId={`${baseTrackingId}-btn-requestremainder`}
            variant='primary'
            {...createRequestTriggerProps}
          >
            {t('Request Remainder')}
          </Button>
          <ConfirmCreateRequestModal invoice={invoice} onCreateRequest={onCreateRequest} {...createRequestModalProps} />
        </>
      );
    }
  }

  return (
    <>
      {!isPaidOrPendingInvoice(invoice) && (
        <>
          <div css={styles.collectPayment}>
            <Button
              trackingId={`${baseTrackingId}-btn-collect`}
              variant={'primary'}
              css={styles.autoWidth}
              onClick={handleCollectPaymentClick}
              disabled={paymentsModalButtonDisabled}
              {...paymentsModalTooltipTriggerProps}
            >
              {isMobile ? t('Collect') : t('Collect Payment')}
            </Button>
            {PaymentsModalTooltip}
            {!isMobile && (
              <>
                <Button
                  trackingId={`${baseTrackingId}-btn-morepopover`}
                  variant='secondary'
                  css={styles.autoWidth}
                  {...menuTriggerProps}
                >
                  <MoreIcon size={16} />
                </Button>
                <PopoverMenu {...menuProps}>
                  {!paymentsModalButtonDisabled && (
                    <>
                      <PopoverMenuItem trackingId={`${baseTrackingId}-menuitem-copy`} Icon={CopyIcon}>
                        <CopyToClipboard text={invoice?.links?.payment || ''} onCopy={() => setCopied(true)}>
                          <span {...tooltipTriggerProps}>{t('Copy Payment Link')}</span>
                        </CopyToClipboard>
                      </PopoverMenuItem>
                      {firstValidPhoneNumber && (
                        <PopoverMenuItem
                          trackingId={`${baseTrackingId}-menuitem-reminder`}
                          Icon={PayIcon}
                          onClick={handleSendReminderClick}
                        >
                          {t('Payment Reminder')}
                        </PopoverMenuItem>
                      )}
                    </>
                  )}
                  <PopoverMenuItem
                    trackingId={`${baseTrackingId}-menuitem-delete`}
                    Icon={DeleteIcon}
                    destructive
                    {...deleteRequestTriggerProps}
                  >
                    {t('Cancel Payment Request')}
                  </PopoverMenuItem>
                </PopoverMenu>
                <Tooltip {...tooltipProps} css={tooltipStyles({ copied })}>
                  {copied ? (
                    <CheckIconSmall className='copy-status-icon' size={16} />
                  ) : (
                    <CopyIcon className='copy-status-icon' size={16} />
                  )}
                  <Text css={{ margin: 0 }} color='white'>
                    {copied ? t('Link copied!') : t('Copy link')}
                  </Text>
                </Tooltip>
              </>
            )}
          </div>
        </>
      )}
      <CollectPaymentMultiModalInstance
        modalProps={collectPaymentModalProps}
        collectProps={{
          invoiceId: invoice.id,
          flow: 'pay',
        }}
      />
      <InvoiceCancelModal
        invoice={invoice}
        modalProps={deleteRequestModalProps}
        onClose={deleteRequestModalProps.onClose}
        onCancel={onRequestCancelled}
      />
    </>
  );
};
